import { Check, Coins, Gift, Lock, Package, Sparkles } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

/**
 * OKS-407 — Cover-first reward card on `/rewards` (W24 design, board
 * `501:180`). Evolves the OKS-60 grid card into a horizontal row:
 *   - 80px cover (URL or a kind-icon fallback tile on null / load
 *     error — cover is an opaque string, never fetched server-side;
 *     TRD KD-1),
 *   - title + state badge (Locked / Coming soon) + "✓ Redeemed" marker,
 *   - description meta,
 *   - amber coin cost (Lucide `Coins` glyph).
 *
 * The whole card is the redeem affordance. It opens the §20 redeem
 * sheet (OKS-557) whenever the reward is NOT a `coming_soon` teaser —
 * so `available` rewards (Confirm / can't-afford / out-of-stock /
 * already-pending sub-states) AND `locked` rewards (coins-to-go) all
 * reach the sheet, which explains the block reason in full. Only a pure
 * `coming_soon` teaser is static + non-interactive.
 *
 * The inline block-reason label (UX Spec §3 / TRD KD-7 precedence:
 * state (coming_soon | locked) > already-requested (pending) >
 * out_of_stock > unaffordable) stays for at-a-glance scannability.
 */

export interface RewardCardReward {
  id: number
  title: string
  description: string
  kind: string
  coin_cost: number
  stock: number | null
  unlimited_stock: boolean
  out_of_stock: boolean
  affordable: boolean
  needs_more: number
  // OKS-407 additions
  state: "available" | "coming_soon" | "locked"
  /**
   * OKS-438 — the 160² `:card` variant. Feeds the SMALL square tiles: this
   * card's `size-20` (80px) cover and the redeem sheet's `size-16` (64px).
   * Never the flagship hero — see `cover_banner_url`.
   */
  cover_image_url: string | null
  /**
   * OKS-837 — the 1280×320 (4:1) `:banner` variant of the SAME cover.
   * Feeds the WIDE creator flagship hero band (`flagship-hero.tsx`
   * `FlagshipCover`, a 640×160 box), where the square `:card` was being
   * upscaled ~4× and cropped twice.
   *
   * Both keys are present on every row and are non-null together — they
   * share one server-side precedence chain (attached blob → variant URL;
   * legacy string → that raw string, identical in both; neither → null).
   * So this is null iff `cover_image_url` is null.
   *
   * REQUIRED, not optional, on purpose: `tsc --noEmit` is the type-level
   * half of the serializer contract pin, so dropping the key server-side
   * reds the FE build and not just the request spec.
   */
  cover_banner_url: string | null
  redeemed: boolean
  pending: boolean
  // OKS-557 — brand-internal live+fulfilled redemption count for the
  // "👥 N redeemed" social-proof meta (count-only; surfaced in the §20
  // redeem sheet header).
  redeemed_count: number
  // OKS-556 — §19 wallet category (Gear · Experiences · Perks). Drives the
  // chip filter + grouped section + the card's category tag.
  category: RewardCategory
}

// OKS-556 — the three wallet category buckets (mirrors `Reward::CATEGORIES`).
export type RewardCategory = "gear" | "experiences" | "perks"

interface RewardCardProps {
  reward: RewardCardReward
  onRedeem: (reward: RewardCardReward) => void
}

const KIND_ICON: Record<string, typeof Gift> = {
  product: Package,
  experience: Sparkles,
  custom: Gift,
}

type BlockReason =
  | "coming_soon"
  | "locked"
  | "already_requested"
  | "out_of_stock"
  | "need_more"
  | null

function blockReasonFor(reward: RewardCardReward): BlockReason {
  if (reward.state === "coming_soon") return "coming_soon"
  if (reward.state === "locked") return "locked"
  if (reward.pending) return "already_requested"
  if (reward.out_of_stock) return "out_of_stock"
  if (!reward.affordable) return "need_more"
  return null
}

export function RewardCard({ reward, onRedeem }: RewardCardProps) {
  const { t } = useTranslation()
  const blockReason = blockReasonFor(reward)
  const redeemable = blockReason === null
  // OKS-557 — the card opens the redeem sheet for anything that isn't a
  // pure `coming_soon` teaser (available in any sub-state + locked). The
  // sheet then drives Confirm / can't-afford / locked / out-of-stock /
  // already-pending. `coming_soon` stays a static teaser.
  const openable = reward.state !== "coming_soon"
  const dimmed = reward.state !== "available"

  const blockLabel =
    blockReason === "need_more"
      ? t("rewards.creator.card.need_more", { count: reward.needs_more })
      : blockReason === "out_of_stock"
        ? t("rewards.creator.card.out_of_stock")
        : blockReason === "already_requested"
          ? t("rewards.creator.card.already_requested")
          : blockReason === "coming_soon"
            ? t("rewards.creator.state.coming_soon")
            : blockReason === "locked"
              ? t("rewards.creator.state.locked")
              : null

  const ariaLabel = redeemable
    ? t("rewards.creator.card.redeem_aria", {
        title: reward.title,
        count: reward.coin_cost,
      })
    : t("rewards.creator.card.blocked_aria", {
        title: reward.title,
        reason: blockLabel ?? "",
      })

  const handleActivate = () => {
    if (openable) onRedeem(reward)
  }

  return (
    <div
      data-testid="reward-card"
      data-reward-id={reward.id}
      data-state={reward.state}
      role={openable ? "button" : undefined}
      tabIndex={openable ? 0 : undefined}
      aria-label={ariaLabel}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (openable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault()
          handleActivate()
        }
      }}
      className={cn(
        "bg-card flex items-stretch gap-3 rounded-xl border p-3 text-left transition-colors",
        openable &&
          "hover:border-primary/40 focus-visible:ring-ring cursor-pointer focus-visible:ring-2 focus-visible:outline-none",
        dimmed && "opacity-60",
      )}
    >
      <RewardCover reward={reward} />

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3
            className="line-clamp-2 text-sm font-semibold"
            title={reward.title}
            data-testid="reward-card-title"
          >
            {reward.title}
          </h3>
          <StateBadge state={reward.state} t={t} />
          {reward.redeemed && (
            <span
              className="text-muted-foreground inline-flex items-center gap-0.5 text-xs font-medium"
              data-testid="reward-card-redeemed"
            >
              <Check className="size-3" aria-hidden="true" />
              {t("rewards.creator.card.redeemed")}
            </span>
          )}
        </div>

        <p className="text-muted-foreground line-clamp-1 text-xs">
          {reward.description}
        </p>

        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span
            className="inline-flex items-center gap-1 text-sm font-semibold text-amber-700 tabular-nums"
            data-testid="reward-card-cost"
          >
            <Coins className="size-4 text-amber-700" aria-hidden="true" />
            {t("rewards.creator.card.coin_count", { count: reward.coin_cost })}
          </span>
          <Badge
            variant="outline"
            className="font-normal"
            data-testid="reward-card-category"
            data-category={reward.category}
          >
            {t(`rewards.creator.categories.${reward.category}`)}
          </Badge>
          {!redeemable && blockLabel && (
            <span
              className="text-muted-foreground text-xs"
              data-testid="reward-card-block-reason"
            >
              {blockLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

function RewardCover({ reward }: { reward: RewardCardReward }) {
  const [errored, setErrored] = useState(false)
  const KindIcon = KIND_ICON[reward.kind] ?? Gift
  const showImage = Boolean(reward.cover_image_url) && !errored

  if (showImage) {
    return (
      <img
        src={reward.cover_image_url!}
        alt={reward.title}
        loading="lazy"
        onError={() => setErrored(true)}
        data-testid="reward-card-cover"
        className="size-20 shrink-0 rounded-lg object-cover"
      />
    )
  }

  return (
    <div
      aria-hidden="true"
      data-testid="reward-card-cover-fallback"
      className="bg-muted text-muted-foreground flex size-20 shrink-0 items-center justify-center rounded-lg"
    >
      <KindIcon className="size-7" />
    </div>
  )
}

function StateBadge({
  state,
  t,
}: {
  state: RewardCardReward["state"]
  t: (key: string) => string
}) {
  if (state === "available") return null

  if (state === "locked") {
    return (
      <Badge
        variant="secondary"
        className="gap-1"
        data-testid="reward-card-state-locked"
      >
        <Lock className="size-3" aria-hidden="true" />
        {t("rewards.creator.state.locked")}
      </Badge>
    )
  }

  return (
    <Badge
      variant="outline"
      className="gap-1"
      data-testid="reward-card-state-coming-soon"
    >
      <Sparkles className="size-3" aria-hidden="true" />
      {t("rewards.creator.state.coming_soon")}
    </Badge>
  )
}
